"use client";

import { useState } from "react";

import { Button } from "@heroui/button";
import { Checkbox } from "@heroui/checkbox";
import { Input } from "@heroui/input";
import { Area } from "@prisma/client";
import { Search } from "lucide-react";

import { EventKeys } from "@/lib/observability/catalog";
import { EventPublisher } from "@/lib/observability/events";

interface ServiceAreaSelectorProps {
  areas: Area[];
  selectedAreaIds: string[];
  onSelectionChange: (areaIds: string[]) => void;
  "data-testid"?: string;
  showLabel?: boolean;
}

/**
 * A simple component for selecting service areas
 */
export function ServiceAreaSelector({
  areas,
  "data-testid": dataTestId,
  onSelectionChange,
  selectedAreaIds,
  showLabel = true,
}: ServiceAreaSelectorProps) {
  const [searchQuery, setSearchQuery] = useState("");

  // Filter areas by search query
  const filteredAreas = areas.filter((area) => {
    const searchLower = searchQuery.toLowerCase();

    return (
      area.name.toLowerCase().includes(searchLower) ||
      area.state.toLowerCase().includes(searchLower)
    );
  });

  // Group areas by state, sorted alphabetically within each state
  const areasByState = filteredAreas.reduce(
    (acc, area) => {
      if (!acc[area.state]) {
        acc[area.state] = [];
      }
      acc[area.state].push(area);

      return acc;
    },
    {} as Record<string, Area[]>,
  );

  for (const state of Object.keys(areasByState)) {
    areasByState[state].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );
  }

  // Handle area selection
  const toggleArea = (areaId: string) => {
    const newSelection = selectedAreaIds.includes(areaId)
      ? selectedAreaIds.filter((id) => id !== areaId)
      : [...selectedAreaIds, areaId];

    onSelectionChange(newSelection);
    void EventPublisher.publishEvent(
      selectedAreaIds.includes(areaId)
        ? EventKeys.SERVICE_AREA_DESELECT
        : EventKeys.SERVICE_AREA_SELECT,
      {
        areaId,
        selected: !selectedAreaIds.includes(areaId),
      },
    );
  };

  // Toggle all areas in a state
  const toggleStateAreas = (state: string, stateAreas: Area[]) => {
    const stateAreaIds = stateAreas.map((area) => area.id);
    const allSelected = stateAreaIds.every((id) =>
      selectedAreaIds.includes(id),
    );

    const newSelection = allSelected
      ? selectedAreaIds.filter((id) => !stateAreaIds.includes(id))
      : [
          ...selectedAreaIds,
          ...stateAreaIds.filter((id) => !selectedAreaIds.includes(id)),
        ];

    onSelectionChange(newSelection);
    void EventPublisher.publishEvent(
      allSelected
        ? EventKeys.SERVICE_AREA_DESELECT_STATE
        : EventKeys.SERVICE_AREA_SELECT_STATE,
      {
        areaIds: stateAreaIds,
        selected: !allSelected,
        state,
      },
    );
  };

  // Check if an area is selected
  const isAreaSelected = (areaId: string) => {
    return selectedAreaIds.includes(areaId);
  };

  return (
    <div className="space-y-4" data-testid={dataTestId}>
      {/* Service Areas Selector */}
      <div className="space-y-2">
        {showLabel && (
          <>
            <label className="text-sm font-medium text-default-700">
              Service Areas <span className="text-danger">*</span>
            </label>
            <p className="text-xs text-default-500">
              Select areas where you'd like to help
            </p>
          </>
        )}

        <div className="border border-default-200 rounded-medium p-4 bg-default-50/30">
          {/* Search */}
          <Input
            classNames={{
              inputWrapper: "min-h-10 rounded-medium",
            }}
            placeholder="Search areas..."
            size="sm"
            startContent={<Search className="h-4 w-4 text-default-400" />}
            value={searchQuery}
            variant="bordered"
            onBlur={() => {
              if (searchQuery) {
                void EventPublisher.publishEvent(
                  EventKeys.SERVICE_AREA_SEARCH,
                  {
                    query: searchQuery,
                  },
                );
              }
            }}
            onValueChange={setSearchQuery}
          />

          {/* Areas grouped by state */}
          <div className="space-y-3 mt-4">
            {Object.entries(areasByState).map(([state, stateAreas]) => {
              const selectedCount = stateAreas.filter((area) =>
                selectedAreaIds.includes(area.id),
              ).length;
              const allSelected = selectedCount === stateAreas.length;

              return (
                <div
                  key={state}
                  className="border border-default-200 rounded-lg p-3 bg-white"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="text-sm font-medium text-default-700">
                        {state}
                      </h4>
                      <p className="text-xs text-default-500">
                        {selectedCount} of {stateAreas.length} areas selected
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        color={allSelected ? "default" : "primary"}
                        size="sm"
                        variant="flat"
                        onPress={() => toggleStateAreas(state, stateAreas)}
                      >
                        {allSelected ? "Deselect All" : "Select All"}
                      </Button>
                      {selectedCount > 0 && (
                        <Button
                          color="default"
                          size="sm"
                          variant="flat"
                          onPress={() => {
                            const stateAreaIds = stateAreas.map(
                              (area) => area.id,
                            );
                            const newSelection = selectedAreaIds.filter(
                              (id) => !stateAreaIds.includes(id),
                            );

                            onSelectionChange(newSelection);
                            void EventPublisher.publishEvent(
                              EventKeys.SERVICE_AREA_CLEAR_STATE,
                              {
                                areaIds: stateAreaIds,
                                state,
                              },
                            );
                          }}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {stateAreas.map((area) => (
                      <div key={area.id} className="flex items-center">
                        <Checkbox
                          classNames={{
                            base: "w-full",
                            wrapper: "z-10 relative",
                          }}
                          data-testid={`service-area-checkbox-${area.name.toLowerCase().replace(/\s+/g, "-")}`}
                          isSelected={isAreaSelected(area.id)}
                          onValueChange={() => toggleArea(area.id)}
                        >
                          <span className="text-sm text-default-700">
                            {area.name}
                          </span>
                        </Checkbox>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary */}
          <div className="text-sm text-default-600 pt-3 border-t border-default-200 mt-4">
            {selectedAreaIds.length} of {areas.length} areas selected
          </div>

          {filteredAreas.length === 0 && (
            <div className="text-center py-6 text-default-500">
              No areas found matching "{searchQuery}"
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
